import { Action } from 'redux';
import { PageViewModel, AppDispatcher } from '@models/common.models';
import { OptionsViewModel } from '@models/common.models';
import { Dispatch } from 'react-redux';
import { AppState } from '../..';
import { ContractCviViewModel, CviOptionsViewModel } from '@models/ServiceWorkOrder';
import { getInventoryList, selectCVI, addNewExclusion } from '../service-work-order.actions';
import { contractService } from '@services/ContractService';
import { cviService } from '@services/CVIService';
import { PropertyTypeViewModel } from '@models/Contract';
import { requestAction } from '@common/components/common.actions';

export enum CVIActionType {
  CVIGetBrandsStart = 'CVIGetTypesStart',
  CVIGetBrandsSuccess = 'CVIGetTypesSuccess',
  CVIGetBrandsFailure = 'CVIGetTypesFailure',

  CVISelectType = 'CVISelectType',

  SaveCVIStart = 'SaveCVIStart',
  SaveCVISuccess = 'SaveCVISuccess',
  SaveCVIFailure = 'SaveCVIFailure',

  LoadCVIForEdit = 'LoadCVIForEdit',
  SetMultipleUnitPropertyTypes = 'SetMultipleUnitPropertyTypes'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

export interface GetCVIBrandsStartAction extends Action {
  readonly type: CVIActionType.CVIGetBrandsStart;
  readonly cviId: number;
}

export interface GetCVIBrandsSuccessAction extends Action {
  readonly type: CVIActionType.CVIGetBrandsSuccess;
  readonly results: OptionsViewModel[];
}
export interface GetCVIBrandsFailureAction extends Action {
  readonly type: CVIActionType.CVIGetBrandsFailure;
  readonly cviId: number;
  readonly errorMessage: string;
}

export interface SelectCVITypeAction extends Action {
  readonly type: CVIActionType.CVISelectType;
  readonly id: number;
  readonly isBrandable: boolean;
}

export interface SaveCVIStartAction extends Action {
  readonly type: CVIActionType.SaveCVIStart;
}

export interface SaveCVISuccessAction extends Action {
  readonly type: CVIActionType.SaveCVISuccess;
}

export interface SaveCVIFailureAction extends Action {
  readonly type: CVIActionType.SaveCVIFailure;
}

export interface LoadCVIForEditAction extends Action {
  readonly type: CVIActionType.LoadCVIForEdit;
  readonly inventoryEntry: ContractCviViewModel;
}

export function getCVITypes(
  contractId: number,
  query: string | undefined,
  id?: number
): Promise<PageViewModel<CviOptionsViewModel>> {
  return contractService.searchCVIs(contractId, query || null, id || null, 10, 1);
}

export function getCVIBrandsStart(id: number): GetCVIBrandsStartAction {
  return {
    type: CVIActionType.CVIGetBrandsStart,
    cviId: id
  };
}

export function getCVIBrandsSuccess(results: OptionsViewModel[]): GetCVIBrandsSuccessAction {
  return {
    type: CVIActionType.CVIGetBrandsSuccess,
    results: results
  };
}

export function getCVIBrandsFailure(id: number, message: string): GetCVIBrandsFailureAction {
  return {
    type: CVIActionType.CVIGetBrandsFailure,
    cviId: id,
    errorMessage: message
  };
}

export function selectCVIType(id: number, isBrandable: boolean): SelectCVITypeAction {
  return {
    type: CVIActionType.CVISelectType,
    id: id,
    isBrandable: isBrandable
  };
}

export function startSaveCVI(): SaveCVIStartAction {
  return {
    type: CVIActionType.SaveCVIStart
  };
}

export function successSaveCVI(): SaveCVISuccessAction {
  return {
    type: CVIActionType.SaveCVISuccess
  };
}

export function failSaveCVI(): SaveCVIFailureAction {
  return {
    type: CVIActionType.SaveCVIFailure
  };
}

export function saveCVI(CVI: ContractCviViewModel) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(startSaveCVI());
    try {
      const response = await contractService.saveCVI(CVI);
      dispatch(getInventoryList(CVI.contractId));

      // Auto select new CVI

      if (response && response instanceof Array) {
        for (let r of response) {
          dispatch(selectCVI(r));
        }
      }

      dispatch(successSaveCVI());
      if (response) {
        const exclusion = {
          comment: 'Replaced',
          exclusionType: { id: 20, value: 'FNHW Replaced' },
          contractCviId: response[0].id,
          contractId: response[0].contractId,
          cvi: response[0].cvi,
          brand: response[0].brand,
          modelNumber: response[0].modelNumber,
          serial: response[0].serial,
          active: false
        };

        dispatch(addNewExclusion(exclusion));
      }
    } catch (e) {
      dispatch(failSaveCVI());
    }
  };
}

export function getBrands(cviId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getCVIBrandsStart(cviId));
    try {
      let result = await cviService.getBrands(cviId);
      getCVIBrandsSuccess(result);
    } catch (e) {
      dispatch(getCVIBrandsFailure(cviId, e));
      return;
    }
    return;
  };
}

/**** Edit CVI ****/
export function editCVI(CVI: ContractCviViewModel) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(startSaveCVI());
    try {
      await contractService.saveCVI(CVI);
      dispatch(getInventoryList(CVI.contractId));
      dispatch(successSaveCVI());
    } catch (e) {
      dispatch(failSaveCVI());
    }
  };
}

export interface SetMultipleUnitPropertyTypesAction extends Action {
  readonly type: CVIActionType.SetMultipleUnitPropertyTypes;
  readonly multipleUnitPropertyTypes: Array<PropertyTypeViewModel>;
}
export function setMultipleUnitPropertyTypes(
  muPropertyTypes: Array<PropertyTypeViewModel>
): SetMultipleUnitPropertyTypesAction {
  const multipleUnitPropertyTypes = muPropertyTypes.filter((value, index, array) => {
    return value.propertyUnitsQt > 1;
  });
  return {
    type: CVIActionType.SetMultipleUnitPropertyTypes,
    multipleUnitPropertyTypes: multipleUnitPropertyTypes
  };
}
export function getMultipleUnitPropertyTypes() {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    return await dispatch(
      requestAction(() => contractService.getPropertyType(), setMultipleUnitPropertyTypes, { hasToast: false })
    );
  };
}



// WEBPACK FOOTER //
// ./src/service-work-order/cvi/cvi.actions.tsx